// Tiny DOM toolkit. Text always goes through text nodes / textContent: there is deliberately no API
// that takes an HTML string. Also holds the small UI builders (buttons, fields, tabs, badges, icons)
// that encode the class names of web/styles/components.css, so every panel looks the same.
import { t } from './i18n/fr.js';

export const SVG_NS = 'http://www.w3.org/2000/svg';

/** Properties set as DOM properties rather than attributes. */
const PROPS = new Set([
  'value', 'checked', 'selected', 'disabled', 'hidden', 'tabIndex', 'readOnly', 'required',
  'multiple', 'indeterminate', 'open', 'htmlFor', 'inert', 'defaultValue', 'spellcheck',
]);
/** Attributes that carry URLs and must never hold a script URL. */
const URL_ATTRS = new Set(['href', 'src', 'action', 'formaction', 'xlink:href', 'poster', 'cite']);
const UNSAFE_URL = /^[\s\u0000-\u001f]*(javascript|vbscript|data|file)\s*:/i;
const SAFE_DATA_IMG = /^data:image\/(png|jpe?g|gif|webp);base64,[a-z0-9+/=\s]+$/i;

let uidCounter = 0;

function doc() {
  return globalThis.document;
}

function isPlainProps(v) {
  return v != null && typeof v === 'object' && !Array.isArray(v) && v.nodeType === undefined;
}

/** Returns true when `value` may be used for the URL attribute `name`. */
export function isSafeUrlAttr(name, value) {
  if (!URL_ATTRS.has(name)) return true;
  // Browsers drop ASCII tab/newline anywhere in a URL ("java\tscript:" is "javascript:"), so test
  // the value the way the URL parser will read it.
  const s = String(value ?? '').replace(/[\t\n\r]/g, '');
  if (name === 'src' && SAFE_DATA_IMG.test(s)) return true;
  return !UNSAFE_URL.test(s);
}

/** Normalises a class prop (string | array | {name: bool}) into a string. */
export function classNames(value) {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.map(classNames).filter(Boolean).join(' ');
  if (typeof value === 'object') return Object.keys(value).filter((k) => value[k]).join(' ');
  return String(value);
}

function setStyle(el, style) {
  if (!style) return;
  if (typeof style === 'string') throw new Error('h(): style must be an object (CSP forbids style attributes)');
  for (const [k, v] of Object.entries(style)) {
    if (v == null || v === false) continue;
    const prop = k.startsWith('--') ? k : k.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase());
    el.style.setProperty(prop, String(v));
  }
}

function applyProps(el, props, isSvg) {
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined || value === null) continue;
    if (key === 'class' || key === 'className') {
      const cls = classNames(value);
      if (cls) el.setAttribute('class', cls);
    } else if (key === 'style') {
      setStyle(el, value);
    } else if (key === 'dataset') {
      for (const [dk, dv] of Object.entries(value)) if (dv != null) el.dataset[dk] = String(dv);
    } else if (key === 'text') {
      el.textContent = String(value);
    } else if (key === 'html' || key === 'innerHTML' || key === 'outerHTML') {
      throw new Error('h(): HTML strings are not supported');
    } else if (key === 'ref') {
      if (typeof value === 'function') value(el);
    } else if (key === 'on' && typeof value === 'object') {
      for (const [type, fn] of Object.entries(value)) if (typeof fn === 'function') el.addEventListener(type, fn);
    } else if (/^on[A-Z]/.test(key)) {
      if (typeof value === 'function') el.addEventListener(key.slice(2).toLowerCase(), value);
    } else if (!isSvg && PROPS.has(key)) {
      el[key] = value;
    } else if (value === false) {
      continue;
    } else {
      const str = value === true ? '' : String(value);
      if (!isSafeUrlAttr(key, str)) continue;
      el.setAttribute(key, str);
    }
  }
}

/** Appends children (strings, numbers, nodes, nested arrays; null/false/true are skipped). */
export function append(parent, ...children) {
  const d = parent.ownerDocument || doc();
  for (const child of children) {
    if (child == null || child === false || child === true) continue;
    if (Array.isArray(child)) { append(parent, ...child); continue; }
    if (typeof child === 'object' && child.nodeType !== undefined) parent.appendChild(child);
    else parent.appendChild(d.createTextNode(String(child)));
  }
  return parent;
}

/**
 * Creates an element: h('button', { class: 'btn', onClick }, 'Texte').
 * Props: class (string | array | object), style (object, CSSOM only), dataset, text, ref(el),
 * on: {type: fn}, onXxx handlers, DOM properties (value, checked, disabled, hidden…), attributes.
 * URL attributes (href, src…) refuse javascript:/data: URLs. HTML strings are never accepted.
 * @param {string} tag
 * @param {object|Node|string|Array} [props]
 * @param {...any} children
 * @returns {HTMLElement}
 */
export function h(tag, props, ...children) {
  const el = doc().createElement(tag);
  if (isPlainProps(props)) applyProps(el, props, false);
  else if (props !== undefined) children.unshift(props);
  return append(el, ...children);
}

/**
 * Creates an SVG element with attributes (class via attribute, styles via CSSOM).
 * @param {string} tag
 * @param {object} [attrs]
 * @param {...any} children
 * @returns {SVGElement}
 */
export function svg(tag, attrs, ...children) {
  const el = doc().createElementNS(SVG_NS, tag);
  if (isPlainProps(attrs)) applyProps(el, attrs, true);
  else if (attrs !== undefined) children.unshift(attrs);
  return append(el, ...children);
}

/** A text node. */
export function text(str) {
  return doc().createTextNode(String(str ?? ''));
}

/** A document fragment holding the given children. */
export function fragment(...children) {
  return append(doc().createDocumentFragment(), ...children);
}

/** Removes every child of `el`; returns `el`. */
export function clear(el) {
  while (el && el.firstChild) el.removeChild(el.firstChild);
  return el;
}

/** Replaces the children of `el`. */
export function replaceChildren(el, ...children) {
  clear(el);
  return append(el, ...children);
}

/** addEventListener that returns its own remover. */
export function on(target, type, fn, opts) {
  target.addEventListener(type, fn, opts);
  return () => target.removeEventListener(type, fn, opts);
}

/** Toggles classes from a {name: bool} map. */
export function classes(el, map) {
  for (const [name, onOff] of Object.entries(map)) el.classList.toggle(name, !!onOff);
  return el;
}

/** Sets textContent only when it changed (avoids layout churn in hot paths). */
export function setText(el, value) {
  const s = value == null ? '' : String(value);
  if (el.textContent !== s) el.textContent = s;
  return el;
}

/** Sets (or removes when null/false) an attribute only when it changed. */
export function setAttr(el, name, value) {
  if (value == null || value === false) {
    if (el.hasAttribute(name)) el.removeAttribute(name);
    return el;
  }
  const s = value === true ? '' : String(value);
  if (!isSafeUrlAttr(name, s)) return el;
  if (el.getAttribute(name) !== s) el.setAttribute(name, s);
  return el;
}

/** querySelector shortcut. */
export function qs(sel, root = doc()) {
  return root.querySelector(sel);
}

/** querySelectorAll as an array. */
export function qsa(sel, root = doc()) {
  return Array.from(root.querySelectorAll(sel));
}

/** A unique id for aria wiring ("mo-3"). */
export function uid(prefix = 'mo') {
  uidCounter += 1;
  return `${prefix}-${uidCounter}`;
}

/** True when the element (or event target) accepts text input. */
export function isEditable(el) {
  if (!el || el.nodeType !== 1) return false;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (el.getAttribute('type') || 'text').toLowerCase();
    return !['button', 'submit', 'reset', 'checkbox', 'radio', 'range', 'color', 'file'].includes(type);
  }
  return !!el.isContentEditable;
}

// ---------------------------------------------------------------------------------------------
// Icons: a small stroke icon set drawn with SVG paths (24×24 grid, currentColor).

const ICONS = {
  close: ['M6 6l12 12', 'M18 6L6 18'],
  plus: ['M12 5v14', 'M5 12h14'],
  minus: ['M5 12h14'],
  paragraph: ['M4 6h16', 'M4 10h16', 'M4 14h16', 'M4 18h11'],
  check: ['M5 12.5l4.2 4.2L19 7'],
  chevronDown: ['M6 9l6 6 6-6'],
  chevronUp: ['M6 15l6-6 6 6'],
  chevronLeft: ['M15 6l-6 6 6 6'],
  chevronRight: ['M9 6l6 6-6 6'],
  arrowRight: ['M5 12h14', 'M13 6l6 6-6 6'],
  arrowLeft: ['M19 12H5', 'M11 18l-6-6 6-6'],
  home: ['M4 11l8-7 8 7', 'M6 9.5V20h12V9.5'],
  building: ['M5 21V5l7-2v18', 'M12 7l7 2v12', 'M3 21h18', 'M8 8h1', 'M8 12h1', 'M8 16h1', 'M15 12h1', 'M15 16h1'],
  layers: ['M12 3l9 5-9 5-9-5 9-5z', 'M3 13l9 5 9-5', 'M3 17.5l9 5 9-5'],
  calendar: ['M4 6h16v14H4z', 'M4 10h16', 'M8 3v4', 'M16 3v4'],
  bolt: ['M13 2L4 14h7l-1 8 9-12h-7l1-8z'],
  repeat: ['M17 2l3 3-3 3', 'M4 11V9a4 4 0 014-4h12', 'M7 22l-3-3 3-3', 'M20 13v2a4 4 0 01-4 4H4'],
  clock: ['M12 21a9 9 0 100-18 9 9 0 000 18z', 'M12 7v5l3 2'],
  user: ['M12 12a4 4 0 100-8 4 4 0 000 8z', 'M4 21a8 8 0 0116 0'],
  key: ['M15 7a4 4 0 11-3.3 6.3L4 21', 'M7 18l2 2', 'M9.5 15.5l2 2'],
  monitor: ['M3 5h18v11H3z', 'M8 21h8', 'M12 16v5'],
  phone: ['M7 2h10v20H7z', 'M11 18h2'],
  sun: ['M12 17a5 5 0 100-10 5 5 0 000 10z', 'M12 1v2', 'M12 21v2', 'M4.2 4.2l1.4 1.4', 'M18.4 18.4l1.4 1.4', 'M1 12h2', 'M21 12h2', 'M4.2 19.8l1.4-1.4', 'M18.4 5.6l1.4-1.4'],
  moon: ['M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z'],
  logout: ['M9 21H5V3h4', 'M16 17l5-5-5-5', 'M21 12H9'],
  settings: ['M12 15a3 3 0 100-6 3 3 0 000 6z', 'M19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.5-1.1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z'],
  tool: ['M14.7 6.3a4 4 0 00-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 005.4-5.4l-2.5 2.5-2.4-.6-.6-2.4 2.5-2.5z'],
  sparkle: ['M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z', 'M19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8L19 16z'],
  lightbulb: ['M12 3a6 6 0 00-3.6 10.8c.7.6 1.1 1.4 1.1 2.2v.5h5V16c0-.8.4-1.6 1.1-2.2A6 6 0 0012 3z', 'M9.5 19h5', 'M10.5 21.5h3'],
  shield: ['M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z'],
  table: ['M3 5h18v14H3z', 'M3 10h18', 'M3 15h18', 'M9 10v9'],
  list: ['M8 6h13', 'M8 12h13', 'M8 18h13', 'M3.5 6h.01', 'M3.5 12h.01', 'M3.5 18h.01'],
  columns: ['M3 4h18v16H3z', 'M9 4v16', 'M15 4v16'],
  inbox: ['M3 13l3-8h12l3 8', 'M3 13v6h18v-6', 'M3 13h5l1.5 3h5l1.5-3h5'],
  send: ['M22 2L11 13', 'M22 2l-7 20-4-9-9-4 20-7z'],
  eye: ['M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z', 'M12 15a3 3 0 100-6 3 3 0 000 6z'],
  eyeOff: ['M3 3l18 18', 'M10.6 5.1A10.9 10.9 0 0112 5c7 0 11 7 11 7a18 18 0 01-3.2 3.9', 'M6.6 6.6A18 18 0 001 12s4 7 11 7a10.8 10.8 0 005.4-1.4', 'M9.9 9.9a3 3 0 004.2 4.2'],
  dots: ['M5 12h.01', 'M12 12h.01', 'M19 12h.01'],
  pencil: ['M12 20h9', 'M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z'],
  film: ['M4 4h16v16H4z', 'M4 9h16', 'M4 15h16', 'M9 4v16', 'M15 4v16'],
  star: ['M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9L12 3z'],
  search: ['M11 18a7 7 0 100-14 7 7 0 000 14z', 'M21 21l-5-5'],
  alert: ['M12 3l10 18H2L12 3z', 'M12 10v5', 'M12 18h.01'],
  bell: ['M12 3v2', 'M6 16v-5a6 6 0 0 1 12 0v5l1.5 2h-15L6 16z', 'M10 20.5a2 2 0 0 0 4 0'],
  info: ['M12 21a9 9 0 100-18 9 9 0 000 18z', 'M12 11v6', 'M12 7.5h.01'],
  wifi: ['M5 12.5a10 10 0 0114 0', 'M8.5 16a5 5 0 017 0', 'M12 19.5h.01', 'M1.5 9a15 15 0 0121 0'],
  panelRight: ['M3 4h18v16H3z', 'M15 4v16'],
  keyboard: ['M2 6h20v12H2z', 'M6 10h.01', 'M10 10h.01', 'M14 10h.01', 'M18 10h.01', 'M7 14h10'],
  hammer: ['M14 4l6 6-3 3-6-6 3-3z', 'M11 7L3 15l3 3 8-8'],
  refresh: ['M21 12a9 9 0 11-3-6.7L21 8', 'M21 3v5h-5'],
  download: ['M12 3v12', 'M7 10l5 5 5-5', 'M4 20h16'],
  upload: ['M12 20V8', 'M7 13l5-5 5 5', 'M4 4h16'],
  paperclip: ['M16 6v10a4 4 0 01-8 0V5a3 3 0 016 0v10a1 1 0 01-2 0V7'],
  image: ['M3 5h18v14H3z', 'M8.5 11a1.5 1.5 0 100-3 1.5 1.5 0 000 3z', 'M21 15l-5-5L5 19'],
  help: ['M12 21a9 9 0 100-18 9 9 0 000 18z', 'M9.6 9.3a2.5 2.5 0 014.8.9c0 1.7-2.4 2.2-2.4 3.6', 'M12 17h.01'],
  external: ['M14 4h6v6', 'M20 4l-9 9', 'M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5'],
  folder: ['M3 6.5A1.5 1.5 0 014.5 5H9l2 2.5h8.5A1.5 1.5 0 0121 9v8.5a1.5 1.5 0 01-1.5 1.5h-15A1.5 1.5 0 013 17.5v-11z'],
  lock: ['M6 11h12v9H6z', 'M8.5 11V8a3.5 3.5 0 017 0v3'],
  plug: ['M9 3v5', 'M15 3v5', 'M6.5 8h11v3.5a5.5 5.5 0 01-11 0V8z', 'M12 17v4'],
};

/**
 * An inline SVG icon (decorative: aria-hidden). Unknown names render an empty 24×24 box.
 * @param {string} name one of the keys of ICONS
 * @param {{size?: number, class?: string, label?: string}} [opts]
 */
export function icon(name, opts = {}) {
  const size = opts.size || 18;
  const el = svg('svg', {
    class: classNames(['icon', `icon-${name}`, opts.class]),
    viewBox: '0 0 24 24', width: size, height: size,
    fill: 'none', stroke: 'currentColor', 'stroke-width': opts.stroke || 1.8,
    'stroke-linecap': 'round', 'stroke-linejoin': 'round', focusable: 'false',
    'aria-hidden': opts.label ? null : 'true', role: opts.label ? 'img' : null, 'aria-label': opts.label || null,
  });
  for (const d of ICONS[name] || []) el.appendChild(svg('path', { d }));
  return el;
}

/** Names of the available icons. */
export const ICON_NAMES = Object.keys(ICONS);

// ---------------------------------------------------------------------------------------------
// UI builders (see docs/UI-CONTRACT.md for the catalogue).

/**
 * A button. variant: 'primary' (black pill) | 'secondary' | 'ghost' | 'danger' | 'amber' | 'link'.
 * @param {string|Node|Array} label
 * @param {{variant?: string, size?: 'sm'|'md'|'lg', icon?: string, iconRight?: string, onClick?: Function,
 *   type?: string, disabled?: boolean, title?: string, ariaLabel?: string, class?: string, busy?: boolean}} [opts]
 */
export function button(label, opts = {}) {
  const variant = opts.variant || 'secondary';
  return h('button', {
    type: opts.type || 'button',
    class: classNames(['btn', `btn-${variant}`, opts.size && `btn-${opts.size}`, opts.class, { 'is-busy': opts.busy }]),
    onClick: opts.onClick, disabled: !!opts.disabled, title: opts.title,
    'aria-label': opts.ariaLabel, 'aria-busy': opts.busy ? 'true' : null,
  }, opts.icon ? icon(opts.icon, { size: 16 }) : null, label != null && label !== '' ? h('span', { class: 'btn-label' }, label) : null,
  opts.iconRight ? icon(opts.iconRight, { size: 16 }) : null);
}

/** The tooltip shown now (one at a time). */
let openTip = null;

/**
 * A « ? » that explains in a bubble on hover, focus or tap — the short way to say why or how,
 * instead of a paragraph on the page. The bubble is placed on the screen (fixed), above the « ? »
 * or below it when there is no room, so a scrolling window never cuts it. Escape closes it.
 * @param {string|Node|Array} text a sentence or two
 * @param {{label?: string, class?: string}} [opts] label: the « ? »'s accessible name (default « En savoir plus »)
 * @returns {HTMLSpanElement}
 */
export function infoTip(text, opts = {}) {
  const id = uid('tip');
  const bubble = h('span', { class: 'tip-bubble', role: 'tooltip', id, hidden: true }, text);
  const place = () => {
    const r = btn.getBoundingClientRect?.();
    if (!r || typeof window === 'undefined') return;
    const vw = window.innerWidth || 0;
    const width = Math.min(300, Math.max(160, vw - 24));
    const left = Math.max(12, Math.min(r.left + r.width / 2 - width / 2, vw - width - 12));
    bubble.style.width = `${width}px`;
    bubble.style.left = `${left}px`;
    const below = r.top < 140;
    bubble.style.top = below ? `${r.bottom + 8}px` : '';
    bubble.style.bottom = below ? '' : `${(window.innerHeight || 0) - r.top + 8}px`;
  };
  // A tap fires mouseenter and focus just before its click: that click must not close the bubble
  // they opened. A tap elsewhere or a scroll closes it (a phone has no mouseleave, iOS no blur).
  let shownAt = 0;
  const outside = (e) => { if (!wrap.contains?.(e.target)) hide(); };
  const scrolled = () => { if (Date.now() - shownAt > 300) hide(); };
  const show = () => {
    if (openTip && openTip !== hide) openTip();
    if (bubble.hidden) {
      shownAt = Date.now();
      globalThis.document?.addEventListener?.('pointerdown', outside, true);
      globalThis.addEventListener?.('scroll', scrolled, true);
    }
    bubble.hidden = false;
    place();
    btn.setAttribute('aria-expanded', 'true');
    openTip = hide;
  };
  function hide() {
    bubble.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    globalThis.document?.removeEventListener?.('pointerdown', outside, true);
    globalThis.removeEventListener?.('scroll', scrolled, true);
    if (openTip === hide) openTip = null;
  }
  const btn = h('button', {
    type: 'button', class: 'tip-btn', 'aria-label': opts.label || t('common.moreInfo'), 'aria-describedby': id, 'aria-expanded': 'false',
    onClick: (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (bubble.hidden) show();
      else if (Date.now() - shownAt > 500) hide();
    },
    onMouseenter: show, onMouseleave: hide, onFocus: show, onBlur: hide,
    onKeydown: (e) => { if (e.key === 'Escape' && !bubble.hidden) { e.stopPropagation(); hide(); } },
  }, icon('help', { size: 16 }));
  const wrap = h('span', { class: classNames(['tip', opts.class]) }, btn, bubble);
  return wrap;
}

/** A round icon-only button with an accessible label. */
export function iconButton(name, label, opts = {}) {
  return h('button', {
    type: 'button', class: classNames(['icon-btn', opts.variant && `icon-btn-${opts.variant}`, opts.class]),
    'aria-label': label, title: opts.title ?? label, onClick: opts.onClick, disabled: !!opts.disabled,
  }, icon(name, { size: opts.size || 18 }));
}

/**
 * A labelled form field wrapper: label + control + hint + error slot.
 * @param {string} label
 * @param {HTMLElement} control an input/select/textarea (its id is generated when missing)
 * @param {{hint?: string, error?: string, class?: string, optional?: boolean}} [opts]
 * @returns {HTMLElement & {setError(msg: string|null): void}}
 */
export function field(label, control, opts = {}) {
  // A wrapper exposing `.input` (e.g. a password field with a toggle) is labelled through its input.
  const target = control.input || control;
  if (!target.id) target.id = uid('f');
  const hintId = uid('hint');
  const errId = uid('err');
  const err = h('p', { class: 'field-error', id: errId, role: 'alert', hidden: !opts.error }, opts.error || '');
  const hint = opts.hint ? h('p', { class: 'field-hint', id: hintId }, opts.hint) : null;
  const describedBy = [hint ? hintId : null, errId].filter(Boolean).join(' ');
  target.setAttribute('aria-describedby', describedBy);
  const wrap = h('div', { class: classNames(['field', opts.class]) },
    h('label', { class: 'field-label', htmlFor: target.id }, label,
      opts.optional ? h('span', { class: 'field-optional' }, t('common.optional')) : null),
    control, hint, err);
  wrap.setError = (msg) => {
    err.textContent = msg || '';
    err.hidden = !msg;
    wrap.classList.toggle('has-error', !!msg);
    if (msg) target.setAttribute('aria-invalid', 'true'); else target.removeAttribute('aria-invalid');
  };
  return wrap;
}

/** A text input with the house style. */
export function input(props = {}) {
  return h('input', { type: 'text', ...props, class: classNames(['input', props.class]) });
}

/** A textarea with the house style. */
export function textarea(props = {}) {
  return h('textarea', { rows: 4, ...props, class: classNames(['input', 'textarea', props.class]) });
}

/**
 * A native select. options: [{value, label, disabled?}] or [[value, label]].
 * @param {Array} options
 * @param {object} [props] extra props (value selects an option)
 */
export function select(options, props = {}) {
  const { value, ...rest } = props;
  const el = h('select', { ...rest, class: classNames(['input', 'select', props.class]) });
  for (const o of options) {
    const [v, l, dis] = Array.isArray(o) ? o : [o.value, o.label, o.disabled];
    el.appendChild(h('option', { value: String(v), disabled: !!dis }, l));
  }
  if (value !== undefined) el.value = String(value);
  return el;
}

/**
 * A switch (checkbox styled as a toggle) with its label.
 * @returns {HTMLLabelElement & {input: HTMLInputElement}}
 */
export function switchToggle(label, { checked = false, onChange, disabled = false, hint } = {}) {
  const box = h('input', { type: 'checkbox', class: 'switch-input', role: 'switch', checked, disabled,
    onChange: (e) => onChange?.(e.target.checked, e) });
  const el = h('label', { class: classNames(['switch', { 'is-disabled': disabled }]) },
    box, h('span', { class: 'switch-track', 'aria-hidden': 'true' }, h('span', { class: 'switch-thumb' })),
    h('span', { class: 'switch-label' }, label, hint ? h('span', { class: 'switch-hint' }, hint) : null));
  el.input = box;
  return el;
}

/**
 * A segmented control (radio group styled as joined pills).
 * @param {Array<{value: string, label: string, icon?: string}>} options
 * @param {{value?: string, onChange?: (value: string) => void, label?: string, size?: 'sm'}} [opts]
 * @returns {HTMLElement & {value: string, setValue(v: string): void}}
 */
export function segmented(options, opts = {}) {
  const el = h('div', { class: classNames(['segmented', opts.size && `segmented-${opts.size}`]), role: 'radiogroup', 'aria-label': opts.label });
  const buttons = new Map();
  const setValue = (v, emit) => {
    el.value = v;
    for (const [val, b] of buttons) {
      b.setAttribute('aria-checked', String(val === v));
      b.tabIndex = val === v ? 0 : -1;
    }
    if (emit) opts.onChange?.(v);
  };
  for (const o of options) {
    const b = h('button', { type: 'button', role: 'radio', class: 'segmented-opt', dataset: { value: o.value },
      onClick: () => setValue(o.value, true) }, o.icon ? icon(o.icon, { size: 14 }) : null, h('span', {}, o.label));
    buttons.set(o.value, b);
    el.appendChild(b);
  }
  el.addEventListener('keydown', (e) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    e.preventDefault();
    const vals = options.map((o) => o.value);
    const idx = vals.indexOf(el.value);
    const next = vals[(idx + (e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 1) + vals.length) % vals.length];
    setValue(next, true);
    buttons.get(next)?.focus();
  });
  el.setValue = (v) => setValue(v, false);
  setValue(opts.value ?? options[0]?.value, false);
  return el;
}

/**
 * Accessible tabs header. Content switching is up to the caller (onChange).
 * @param {Array<{id: string, label: string, count?: number}>} items
 * @param {{active?: string, onChange?: (id: string) => void, label?: string}} [opts]
 * @returns {HTMLElement & {setActive(id: string): void, setCount(id: string, n: number|null): void}}
 */
export function tabs(items, opts = {}) {
  const el = h('div', { class: 'tabs', role: 'tablist', 'aria-label': opts.label });
  const map = new Map();
  const setActive = (id, emit) => {
    for (const [tid, b] of map) {
      b.setAttribute('aria-selected', String(tid === id));
      b.tabIndex = tid === id ? 0 : -1;
    }
    el.dataset.active = id;
    if (emit) opts.onChange?.(id);
  };
  for (const it of items) {
    const count = h('span', { class: 'tab-count', hidden: it.count == null }, it.count ?? '');
    const b = h('button', { type: 'button', role: 'tab', class: 'tab', id: uid('tab'), dataset: { tab: it.id },
      onClick: () => setActive(it.id, true) }, h('span', { class: 'tab-label' }, it.label), count);
    b.countEl = count;
    map.set(it.id, b);
    el.appendChild(b);
  }
  el.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const ids = items.map((i) => i.id);
    const idx = ids.indexOf(el.dataset.active);
    const next = ids[(idx + (e.key === 'ArrowLeft' ? -1 : 1) + ids.length) % ids.length];
    setActive(next, true);
    map.get(next)?.focus();
  });
  el.setActive = (id) => setActive(id, false);
  el.setCount = (id, n) => {
    const b = map.get(id);
    if (!b) return;
    b.countEl.hidden = n == null;
    setText(b.countEl, n ?? '');
  };
  setActive(opts.active ?? items[0]?.id, false);
  return el;
}

/** A small uppercase badge. tone: 'neutral' | 'amber' | 'success' | 'danger' | 'info' | 'dark'. */
export function badge(label, tone = 'neutral', opts = {}) {
  return h('span', { class: classNames(['badge', `badge-${tone}`, opts.class]), title: opts.title }, label);
}

const STATE_TONES = {
  scheduled: 'info', queued: 'neutral', running: 'running', executing: 'running',
  awaiting_approval: 'amber', done: 'success', failed: 'danger', rejected: 'muted', cancelled: 'muted',
  blocked: 'amber', todo: 'amber', ignored: 'muted', resolved: 'success',
};

/**
 * The state shown for a task: its state, 'blocked' for a done task stopped by an external cause
 * (« BLOQUÉ : … »), 'todo' for a done task that waits for a person (« À RÉGLER : … », until it is
 * marked settled) — SPEC-EQUIPE §3; 'ignored' for a problem a person chose to ignore, 'resolved'
 * for a failure or a blocking a later run of its routine made good (`superseded`): neither is an
 * error any more.
 * @param {object|null|undefined} task
 * @returns {string}
 */
export function taskState(task) {
  if (!task || typeof task !== 'object') return '';
  if (task.ignored) return 'ignored';
  if (task.superseded && (task.state === 'failed' || (task.state === 'done' && task.blocked))) return 'resolved';
  if (task.state !== 'done') return task.state;
  if (task.blocked) return 'blocked';
  return task.todo && !task.settled ? 'todo' : 'done';
}

/** The badge for a task state ("EN COURS", "À VALIDER", "BLOQUÉE"…): a state or a task. */
export function stateBadge(stateOrTask) {
  const state = stateOrTask && typeof stateOrTask === 'object' ? taskState(stateOrTask) : stateOrTask;
  return h('span', { class: classNames(['badge', `badge-${STATE_TONES[state] || 'neutral'}`, 'state-badge']), dataset: { state } },
    t(`state.${state}`));
}

/** A status dot for agents or connectors (idle | working | waiting | error | done | ok | warn | off). */
export function statusDot(status, label) {
  return h('span', { class: classNames(['status-dot', `is-${status || 'idle'}`]), role: label ? 'img' : null,
    'aria-label': label || null, 'aria-hidden': label ? null : 'true' });
}

/** A filter chip (toggle button). */
export function chip(label, { active = false, count = null, tone, onClick, dot } = {}) {
  return h('button', { type: 'button', class: classNames(['chip', tone && `chip-${tone}`, { 'is-active': active }]),
    'aria-pressed': String(!!active), onClick },
  dot ? h('span', { class: 'chip-dot', style: { '--dot': dot } }) : null,
  h('span', { class: 'chip-label' }, label), count != null ? h('span', { class: 'chip-count' }, count) : null);
}

/** An empty state block: icon, title, text and optional action button. */
export function emptyState({ icon: iconName = 'inbox', title, text: body, action } = {}) {
  return h('div', { class: 'empty-state' },
    h('div', { class: 'empty-icon' }, icon(iconName, { size: 22 })),
    title ? h('p', { class: 'empty-title' }, title) : null,
    body ? h('p', { class: 'empty-text' }, body) : null,
    action || null);
}

/** A small spinner with an accessible label. */
export function spinner(label = t('common.loading')) {
  return h('span', { class: 'spinner', role: 'status', 'aria-label': label });
}

/** A keyboard key hint. */
export function kbd(key) {
  return h('kbd', { class: 'kbd' }, key);
}

/** A card container (surface, radius, hairline, shadow). */
export function card(...children) {
  return h('div', { class: 'card' }, ...children);
}

/** A monospace uppercase label ("EN COURS"). */
export function monoLabel(label, props = {}) {
  return h('span', { ...props, class: classNames(['mono-label', props.class]) }, label);
}
